import { JsonObjectSchema, type JsonObject } from '@inf-monkeys-tech/monkeys';
import { createRecordEditorDraft } from '@inf-monkeys-tech/monkeys/runtime';
import type { CapabilityTranslate } from './contracts';
import { editorLeafFields, editorObjects, projectEditorFields } from './recordEditorDraft';
import { resolveI18nDisplayText, resolveLocalizedDisplayParameters } from './localizedDisplayValue';

/** Project the same field presentation and conditions without exposing secret leaves. */
export function projectReadOnlyEditorFields(model: JsonObject, translate: CapabilityTranslate, locale = 'en'): JsonObject[] {
  const source = editorObjects(model.fields);
  const presentations = new Map(editorObjects(model.fieldPresentation).map(field => [field.id, field]));
  const byId = new Map(source.map(field => [field.id, field]));
  const selected = Array.isArray(model.visibleFieldIds)
    ? model.visibleFieldIds.flatMap(id => typeof id === 'string' && byId.has(id) ? [byId.get(id)!] : []) : source;
  const fields = selected.filter(field => field.kind !== 'secret').map(field => {
    const merged: JsonObject = { ...field, ...presentations.get(field.id), id: field.id, value: field.value ?? null };
    return merged.kind === 'select' && typeof merged.fallbackValue === 'string' && merged.fallbackValue &&
      !editorObjects(merged.options).some(option => option.id === String(merged.value ?? ''))
      ? { ...merged, value: merged.fallbackValue } : merged;
  });
  const withoutSecrets = (items: JsonObject[]): JsonObject[] => items.filter(field => field.kind !== 'secret').map(field =>
    field.kind === 'object' || field.kind === 'array' ? { ...field, children: withoutSecrets(editorObjects(field.children)) } : field);
  const safeFields = withoutSecrets(fields);
  const envelope = model.draft;
  const hasDraft = envelope && typeof envelope === 'object' && !Array.isArray(envelope);
  // Legacy value-only forms have a flat field contract; nested stable paths require a draft.
  if (!hasDraft && safeFields.some(field => field.kind === 'object' || field.kind === 'array' || field.path !== undefined)) {
    throw new TypeError('Nested read-only editor fields require a draft.');
  }
  const value = hasDraft ? envelope.value : Object.fromEntries(safeFields.map(field => [String(field.id), field.value ?? null]));
  const draft = createRecordEditorDraft(hasDraft && typeof envelope.revision === 'string' ? envelope.revision : 'read-only', JsonObjectSchema.parse(value));
  return editorLeafFields(projectEditorFields(safeFields, draft, [], model)).filter(field => !field.hidden && field.kind !== 'secret').map(field => {
    const displayLabel = (item: JsonObject, fallback: string) => item.labelI18n !== undefined
      ? resolveI18nDisplayText(item.labelI18n, locale)
      : typeof item.labelKey === 'string' && item.labelKey
        ? translate(item.labelKey, resolveLocalizedDisplayParameters(item.labelParameters, locale))
        : typeof item.label === 'string' ? item.label : fallback;
    const label = (value: unknown) => {
      const option = editorObjects(field.options).find(option => option.id === value);
      return option ? displayLabel(option, String(value)) : String(value ?? '');
    };
    let value = field.kind === 'select' ? label(field.value) : field.kind === 'multi-select' && Array.isArray(field.value) ? field.value.map(label).join(', ') : field.value ?? null;
    if (value && typeof value === 'object' && !Array.isArray(value) && ['relations', 'tags', 'files'].includes(String(value.kind))) {
      value = { ...value, items: editorObjects(value.items).map(item => {
        const projected: JsonObject = { ...item, label: displayLabel(item, String(item.id ?? '')) };
        delete projected.labelKey; delete projected.labelParameters; delete projected.labelI18n;
        return projected;
      }) };
    }
    const projected: JsonObject = { ...field, label: displayLabel(field, String(field.id)), value };
    delete projected.labelKey; delete projected.labelParameters; delete projected.labelI18n;
    return projected;
  });
}
